import SortArrows from '@rio-cloud/rio-uikit/SortArrows';
import classNames from 'classnames';
import { type ReactNode, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ProductTypedFormattedMessage } from '../../common/product/ProductTypedFormattedMessage';
import { ProductType } from '../../common/product/product';
import type { TermedLevel } from '../../common/termedLevel/types';
import { DATE_FORMAT } from '../../common/utils/dateUtil';
import { formatDurationUnit } from '../../common/utils/formatDurationUnit';
import { ContractResourceName } from '../contracts/ContractResourceName';
import { ContractState } from '../contracts/ContractState';
import { ContractTableRowRenewalText } from '../contracts/ContractTableRowRenewalText';
import { useSorting } from '../contracts/useSorting';

interface Props {
    contracts: TermedLevel[];
    productType: ProductType;
}

interface Column {
    id: string;
    propertyName: keyof TermedLevel;
    label: ReactNode;
    width?: string;
}

const tableClassName = classNames(
    'table',
    'table-layout-fixed',
    'table-column-overflow-hidden',
    'table-condensed',
    'table-sticky',
    'table-sticky-in-container',
    'table-hover',
    'table-head-filled',
    'margin-bottom-0'
);

function getColumns(productType: ProductType): Column[] {
    return [
        {
            id: 'resourceName',
            propertyName: 'resourceName',
            label: (
                <ProductTypedFormattedMessage
                    productType={productType}
                    userBasedId='intl-msg:marketplace.contracts.user.table.header'
                    assetBasedId='intl-msg:marketplace.contracts.asset.table.header'
                />
            ),
            width: '17%',
        },
        {
            id: 'variantName',
            propertyName: 'variantName',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.variant' />,
            width: '10%',
        },
        {
            id: 'periodLength',
            propertyName: 'periodLength',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.period' />,
            width: '12%',
        },
        {
            id: 'startsAt',
            propertyName: 'startsAt',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.startDate' />,
            width: '13%',
        },
        {
            id: 'endsAt',
            propertyName: 'endsAt',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.endDate' />,
            width: '13%',
        },
        {
            id: 'remainingDays',
            propertyName: 'remainingDays',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.daysLeft' />,
            width: '11%',
        },
        {
            id: 'contractState',
            propertyName: 'contractState',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.status' />,
            width: '10%',
        },
        {
            id: 'renewsToLevelName',
            propertyName: 'renewsToLevelName',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.renewsTo' />,
            width: '14%',
        },
    ];
}

function ContractPeriod({ contract }: { contract: TermedLevel }) {
    const intl = useIntl();
    return (
        <span className='label label-condensed label-default'>
            {contract.periodLength} {formatDurationUnit(contract.periodUnit, intl, contract.periodLength)}
        </span>
    );
}

function ContractDate({ value }: { value?: string }) {
    const intl = useIntl();
    return value ? intl.formatDate(value, DATE_FORMAT) : <span>–</span>;
}

function getCompactVariantName(contract: TermedLevel): string {
    const variantName = contract.variantName.replace(/\s+variant$/i, '').trim();
    const productName = contract.productName.trim();

    if (variantName.toLocaleLowerCase().startsWith(`${productName.toLocaleLowerCase()} `)) {
        return variantName.slice(productName.length).trim();
    }
    return variantName;
}

function ContractVariant({ contract }: { contract: TermedLevel }) {
    return <span className='label label-condensed label-primary label-filled'>{getCompactVariantName(contract)}</span>;
}

function RemainingDays({ contract }: { contract: TermedLevel }) {
    if (contract.remainingDays === undefined) {
        return <span>–</span>;
    }
    if (contract.remainingDays === 0) {
        return (
            <span className='text-color-warning text-medium'>
                <FormattedMessage id='intl-msg:marketplace.today' />
            </span>
        );
    }
    return (
        <span className={classNames({ 'text-color-warning text-medium': contract.criticalRemainingDays })}>
            {contract.remainingDays}{' '}
            <FormattedMessage
                id={
                    contract.remainingDays === 1
                        ? 'intl-msg:marketplace.durationUnit.day'
                        : 'intl-msg:marketplace.durationUnit.days'
                }
            />
        </span>
    );
}

function ContractTableRow({ contract, productType }: { contract: TermedLevel; productType: ProductType }) {
    return (
        <tr>
            <td>
                <span
                    className={classNames(
                        'uikit-icon',
                        productType === ProductType.USER_BASED ? 'icon-user' : 'icon-truck',
                        'margin-right-5'
                    )}
                    aria-hidden={true}
                />
                <ContractResourceName contract={contract} />
            </td>
            <td>
                <ContractVariant contract={contract} />
            </td>
            <td>
                <ContractPeriod contract={contract} />
            </td>
            <td>
                <ContractDate value={contract.startsAt} />
            </td>
            <td>
                <ContractDate value={contract.endsAt} />
            </td>
            <td>
                <RemainingDays contract={contract} />
            </td>
            <td className='text-capitalize'>
                <ContractState contractState={contract.contractState} />
            </td>
            <td>
                <ContractTableRowRenewalText contract={contract} />
            </td>
        </tr>
    );
}

export function BookingOverviewContractTable({ contracts, productType }: Props) {
    const { sort, handleSortChange, sortDirectionForProperty } = useSorting<TermedLevel>('resourceName');
    const columns = useMemo(() => getColumns(productType), [productType]);

    return (
        <div className='max-height-50vh width-100pct overflow-y-auto'>
            <table className={tableClassName}>
                <colgroup>
                    {columns.map(column => (
                        <col key={column.id} style={{ width: column.width }} />
                    ))}
                </colgroup>
                <thead>
                    <tr>
                        {columns.map(column => (
                            <th
                                key={column.id}
                                className='sort-column'
                                onClick={() => handleSortChange(column.propertyName)}
                            >
                                <span>
                                    <SortArrows direction={sortDirectionForProperty(column.propertyName)} />
                                    {column.label}
                                </span>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sort(contracts).map(contract => (
                        <ContractTableRow
                            key={`${contract.productId}-${contract.resourceId}-${contract.level}-${contract.createdAt}`}
                            contract={contract}
                            productType={productType}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
}
